
import React from "react";
import { Link } from "react-router-dom";

const PrivacyPolicy = () => {
  return (
    <div
      className="min-h-screen py-6 md:py-8 px-4 sm:px-6 lg:px-8"
      style={{ backgroundColor: "#F5F3F0" }}
    >
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-6 md:mb-8">
          <Link to="/" className="inline-block mb-4 md:mb-6">
            <img
              src="/images/gemterra-logo-20260112-t.png"
              alt="Gemterra Logo"
              className="h-10 md:h-12 mx-auto"
            />
          </Link>
          <h1
            className="text-2xl md:text-3xl font-bold mb-3 md:mb-4"
            style={{ color: "#333333" }}
          >
            Privacy Policy
          </h1>
          <p style={{ color: '#A89F91' }} className="text-sm md:text-base">
            Last updated: April 2026
          </p>
        </div>

        {/* Content */}
        <div className="bg-white rounded-lg shadow-sm p-4 md:p-8 space-y-6 md:space-y-8">
          {/* iOS App Section */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">
              Privacy Policy - Gemterra Site (iOS App)
            </h2>

            <div className="space-y-6">
              <div>
                <p className="text-gray-700 mb-4">
                  Gemterra Site is an internal application developed by Gemterra
                  for use by its employees and authorized contractors. This
                  Privacy Policy explains how we collect, use, and protect
                  information within the app.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-medium text-gray-800 mb-3">Information We Collect</h3>
                <ul className="list-disc list-inside text-gray-700 space-y-2">
                  <li>
                    Device information (such as device model and operating
                    system version)
                  </li>
                  <li>
                    Authentication information (such as work email and assigned
                    user role, e.g., site, office, PM)
                  </li>
                  <li>User-submitted content, including:</li>
                  <li className="ml-5">Site updates</li>
                  <li className="ml-5">Task notes</li>
                  <li className="ml-5">Uploaded photos</li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-medium text-gray-800 mb-3">
                  Location Data
                </h3>
                <p className="text-gray-700 mb-3">
                  The app may access your device&apos;s location while you are
                  using the app in order to retrieve local weather information
                  for site reports.
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-2">
                  <li>Location data is used only at the time of request</li>
                  <li>
                    Location data is not stored, transmitted, or shared outside
                    the device
                  </li>
                  <li>Weather data is provided by Apple WeatherKit</li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-medium text-gray-800 mb-3">
                  How We Use the Information
                </h3>
                <ul className="list-disc list-inside text-gray-700 space-y-2">
                  <li>
                    To synchronize site reports with project managers
                  </li>
                  <li>
                    To update project schedule status in real time
                  </li>
                  <li>To support internal documentation and reporting</li>
                  <li>
                    To enhance the functionality and performance of the app
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-medium text-gray-800 mb-3">Data Sharing</h3>
                <p className="text-gray-700">
                  Data is stored securely and is accessible only to authorized
                  team members within Gemterra.
                </p>
                <p className="text-gray-700 mt-2">
                  We do not sell, rent, or share data with third parties for
                  marketing purposes.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-medium text-gray-800 mb-3">
                  Data Storage and Security
                </h3>
                <p className="text-gray-700 mb-3">
                  We implement reasonable administrative and technical measures
                  to protect:
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-2">
                  <li>User-submitted content</li>
                  <li>Authentication credentials</li>
                  <li>Internal project data</li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-medium text-gray-800 mb-3">
                  Third-Party Services
                </h3>
                <p className="text-gray-700">
                  We may use secure infrastructure services such as cloud
                  storage providers and authentication services. These services
                  are used solely to support internal operations and data
                  storage.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-medium text-gray-800 mb-3">
                  Your Rights
                </h3>
                <p className="text-gray-700">
                  As this is an internal enterprise tool, access to data is
                  managed by Gemterra. Users may contact the administrator for
                  any data-related requests.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-medium text-gray-800 mb-3">
                  Contact
                </h3>
                <p className="text-gray-700">
                  For any privacy-related questions, please contact:
                </p>
                <p className="text-gray-700 font-medium">info@gemterra.com</p>
              </div>
            </div>
          </section>

          {/* Web App Section */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">
              Privacy Policy - Gemterra Web App (project.gemterra.com)
            </h2>

            <div className="space-y-6">
              <div>
                <p className="text-gray-700 mb-4">
                  This Privacy Policy describes how Gemterra collects and uses
                  information through the Gemterra web platform.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-medium text-gray-800 mb-3">Information We Collect</h3>
                <ul className="list-disc list-inside text-gray-700 space-y-2">
                  <li>
                    Account registration details (such as email and name)
                  </li>
                  <li>User-submitted content, including:</li>
                  <li className="ml-5">Task notes</li>
                  <li className="ml-5">Uploaded files or images</li>
                  <li>
                    Usage data (such as page visits and interactions)
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-medium text-gray-800 mb-3">Use of Information</h3>
                <ul className="list-disc list-inside text-gray-700 space-y-2">
                  <li>Enable login and account access</li>
                  <li>Store and retrieve project-related information</li>
                  <li>Support internal project coordination</li>
                  <li>Improve system performance and usability</li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-medium text-gray-800 mb-3">Third-Party Services</h3>
                <p className="text-gray-700 mb-3">
                  We may use trusted third-party services for infrastructure and
                  operations, including:
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-2">
                  <li>Supabase (authentication and database)</li>
                  <li>MongoDB Atlas (data storage)</li>
                  <li>Amazon S3 (file storage)</li>
                </ul>
                <p className="text-gray-700 mt-3">
                  These providers process data only on behalf of Gemterra.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-medium text-gray-800 mb-3">
                  Analytics
                </h3>
                <p className="text-gray-700">
                  We may use analytics tools (such as Google Analytics) to
                  understand usage patterns and improve the service. No
                  personally identifiable information is used for advertising
                  purposes.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-medium text-gray-800 mb-3">Cookies</h3>
                <p className="text-gray-700 mb-3">Cookies may be used to:</p>
                <ul className="list-disc list-inside text-gray-700 space-y-2">
                  <li>Maintain login sessions</li>
                  <li>Support basic functionality</li>
                  <li>Enable analytics (if applicable)</li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-medium text-gray-800 mb-3">
                  Data Sharing
                </h3>
                <p className="text-gray-700">
                  Data is accessible only to authorized users within the
                  organization.
                </p>
                <p className="text-gray-700 mt-2">
                  We do not sell or share user data with third parties for
                  marketing purposes.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-medium text-gray-800 mb-3">
                  Data Security
                </h3>
                <p className="text-gray-700 mb-3">
                  We apply reasonable security practices to protect:
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-2">
                  <li>User accounts</li>
                  <li>Uploaded content</li>
                  <li>System access</li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-medium text-gray-800 mb-3">
                  Contact
                </h3>
                <p className="text-gray-700">
                  For any questions regarding this policy:
                </p>
                <p className="text-gray-700 font-medium">info@gemterra.com</p>
              </div>
            </div>
          </section>
        </div>

        {/* Back to Login Link */}
        <div className="text-center mt-6 md:mt-8">
          <Link
            to="/"
            className="inline-flex items-center px-4 py-2 text-sm font-medium transition duration-200"
            style={{ color: "#224228" }}
          >
            ← Back to Login
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
